import { AnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentContext';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { createContextConfig } from '@/domain/mitarbeiter/features/anlageassistent/context/utils';
import { useSubStepSchemasMap } from '@/domain/mitarbeiter/features/anlageassistent/hooks/schema/useFormSchema';
import {
  AnlageassistentContextType,
  Step,
  StepKey,
  SubStep,
  SubStepKey,
} from '@/domain/mitarbeiter/features/anlageassistent/types/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line max-lines-per-function
function AnlageassistentContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const form = useAnlageassistentFormContext();
  const subStepSchemasMap = useSubStepSchemasMap();
  const [anlageassistentState, setAnlageassistentState] = useState<
    AnlageassistentContextType['state']
  >(createContextConfig({ t }));

  function getCurrentStep(): Step {
    return anlageassistentState.steps[anlageassistentState.currentStepIndex];
  }

  function validateStepReady(step: Step): boolean {
    if (step.type === 'summary') {
      return false;
    }

    return step.subSteps.every((subStep) => {
      const parseResult = subStepSchemasMap[subStep.key].safeParse(
        form.getFieldValue(subStep.key),
      );
      return parseResult.success;
    });
  }

  async function validateCurrentSubStep(): Promise<boolean> {
    const currentStep = getCurrentStep();
    if (currentStep.type === 'summary') {
      return true;
    }
    const currentSubStep =
      currentStep.subSteps[anlageassistentState.currentSubStepIndex];

    const parseResult = subStepSchemasMap[currentSubStep.key].safeParse(
      form.getFieldValue(currentSubStep.key),
    );

    // field errorMap muss manuell gesetzt werden, da durch form validate die gesamte form validiert wird.
    if (!parseResult.success) {
      type FieldPath = Parameters<typeof form.setFieldMeta>[0];
      parseResult.error.issues.forEach((issue) => {
        const fieldpath = (
          issue.path.length === 0
            ? currentSubStep.key
            : currentSubStep.key + '.' + issue.path.join('.')
        ) as FieldPath;
        form.setFieldMeta(fieldpath, (prev) => ({
          ...prev,
          errorMap: {
            onSubmit: issue,
          },
        }));
      });
    }

    return parseResult.success;
  }

  async function navigateToStep(step: StepKey | number) {
    const stepIndex =
      typeof step === 'number'
        ? step
        : anlageassistentState.steps.findIndex((s) => s.key === step);
    if (stepIndex === -1) {
      return;
    }

    if (stepIndex > anlageassistentState.currentStepIndex) {
      if ((await validateCurrentSubStep()) === false) {
        return;
      }
    }

    setAnlageassistentState((prev) => ({
      ...prev,
      currentStepIndex: stepIndex,
      currentSubStepIndex: 0,
    }));
  }

  async function navigateToSubStep(subStepKey: SubStepKey) {
    anlageassistentState.steps.forEach((step) => {
      if (step.type === 'summary') {
        return;
      }
      step.subSteps.forEach(async (subStep) => {
        if (subStep.key === subStepKey) {
          await navigateToStep(step.key);
          setAnlageassistentState((prev) => ({
            ...prev,
            currentSubStepIndex: step.subSteps.findIndex(
              (s) => s.key === subStepKey,
            ),
          }));
          return;
        }
      });
    });
  }

  function hasNextStep(): boolean {
    return (
      anlageassistentState.currentStepIndex <
      anlageassistentState.steps.length - 1
    );
  }

  function hasNextSubStep(): boolean {
    const currentStep =
      anlageassistentState.steps[anlageassistentState.currentStepIndex];
    if (currentStep.type === 'summary') {
      return false;
    }
    return (
      anlageassistentState.currentSubStepIndex < currentStep.subSteps.length - 1
    );
  }

  function hasPreviousStep(): boolean {
    return anlageassistentState.currentStepIndex > 0;
  }

  function hasPreviousSubStep(): boolean {
    return anlageassistentState.currentSubStepIndex > 0;
  }

  async function next() {
    if (hasNextSubStep()) {
      if (await validateCurrentSubStep()) {
        setAnlageassistentState((prev) => ({
          ...prev,
          currentSubStepIndex: prev.currentSubStepIndex + 1,
        }));
      }
    } else if (hasNextStep()) {
      if (await validateCurrentSubStep()) {
        setAnlageassistentState((prev) => ({
          ...prev,
          currentStepIndex: prev.currentStepIndex + 1,
          currentSubStepIndex: 0,
        }));
      }
    }
  }

  function previous() {
    if (hasPreviousSubStep()) {
      setAnlageassistentState((prev) => ({
        ...prev,
        currentSubStepIndex: prev.currentSubStepIndex - 1,
      }));
    } else if (hasPreviousStep()) {
      setAnlageassistentState((prev) => {
        const previousStep = prev.steps[prev.currentStepIndex - 1];
        return {
          ...prev,
          currentStepIndex: prev.currentStepIndex - 1,
          currentSubStepIndex:
            previousStep.type === 'summary'
              ? 0
              : previousStep.subSteps.length > 0
                ? previousStep.subSteps.length - 1
                : 0,
        };
      });
    }
  }

  function currentStepProgress(): number | null {
    const currentStep =
      anlageassistentState.steps[anlageassistentState.currentStepIndex];
    if (currentStep.type === 'summary') {
      return null;
    }
    return (
      ((anlageassistentState.currentSubStepIndex + 1) /
        (currentStep.subSteps.length > 0 ? currentStep.subSteps.length : 1)) *
      100
    );
  }

  function getCurrentSubStep(): SubStep | null {
    const currentStep = getCurrentStep();
    if (currentStep.type === 'summary') {
      return null;
    }
    return currentStep.subSteps[anlageassistentState.currentSubStepIndex];
  }

  return (
    <AnlageassistentContext.Provider
      value={{
        state: anlageassistentState,
        setState: setAnlageassistentState,
        navigateToStep,
        navigateToSubStep,
        validateStepReady,
        next,
        previous,
        hasNext: hasNextStep,
        hasPrevious: () => hasPreviousStep() || hasPreviousSubStep(),
        currentStepProgress,
        getCurrentSubStep,
      }}>
      {children}
    </AnlageassistentContext.Provider>
  );
}

export { AnlageassistentContextProvider };
